import {
	readContracts,
	getChainId as getWagmiChainId,
} from "wagmi/actions";
import { wagmiConfig, } from "@config";
import * as C from "@contracts";

import type {
	ChainId,
	TokenDefinition,
} from "@bracket/addressbook";

import type {
	ResolverErrorResult,
} from "./types";



/** get the value of a bigint
 * @summary - BigInt / 1e18; 
 * @returns {Number} */
export const n = ( value: bigint ) => Number(value) / 1e18;



/** return numeric value as bigint
 * @param { number | string } value number, float or string representation of the value
 * @summary floors value * 1e18 then returns as bigint
 * @returns {BigInt} */
export const bi = ( value: number | string ): bigint => {
	try {
		value = typeof value === "string" && value.endsWith(".") ? `${value}0` : value;
		const valueNumber = typeof value === "number" ? value : Number.parseFloat(value);
		return BigInt(Math.floor(valueNumber * 1e18));
	}
	catch( err ) {
		throw new Error(`failed converting to bigint value "${value.toString()}"`);
	}
}


export declare type ContractReversionError = {
	message: string;
	cause: {
		reason: string;
		code: number;
	}
}


// biome-ignore lint/suspicious/noExplicitAny: time saver
export const isContractReversionError = ( value: any, ): value is ContractReversionError => {
	if(typeof value === "object" && !Array.isArray(value) && value !== null) {
		if( "cause" in value && "reason" in value.cause ) {
			return true;
		}
	}
	return false;
}

export declare type ErrorWithCause = Error & {
	cause: {
		name: string;
		reason?: string;
		code?: number;
	}
}

// biome-ignore lint/suspicious/noExplicitAny: time saver
export const isErrorWithCause = ( value: any ): value is ErrorWithCause => {
	if(typeof value === "object" && !Array.isArray(value) && value !== null) {
		if( "cause" in value ) {
			if( "name" in value.cause ) {
				return true;
			}
		}
	}
	return false;
}

export const isChainId = ( value:unknown ): value is ChainId => typeof value === "number" && value > 0;


export const getChainId = (): ChainId => {
	const chainId = getWagmiChainId(wagmiConfig);
	if( chainId && isChainId( chainId ) ) {
		return chainId;
	}
	throw new Error("@bracket/stake-app/resolvers::getChainId failed to produce a valid chain id");
}

/**
 * 
 * @param param0 
 * @returns 
 */
export const findValidFee = async ({
	token,
	amount,
}: { token: TokenDefinition, amount: string | number; } ): Promise<number> => {

	const nAmount = typeof amount === "string" ? Number(amount) : amount;
	const { fees, } = token;

	if( fees === undefined ) throw new Error(`token "${token.label}" does not have any fee set.`)
	const feesArr = Array.isArray(fees) ? fees : [ fees ];

	if( Array.isArray(feesArr) && feesArr.length ) {

		const quotes = await readContracts(wagmiConfig, {
			contracts: feesArr.map(fee => C.IEscrowRouter.uniswapQuote(Object.assign({}, token, { fee, }), nAmount)),
		});

		if(Array.isArray(quotes)) {
			let idx = 0;
			let feeIdx = -1;
			let zquote = -1;
			for( const quote of quotes ) {
				if( quote.status === "success" && Number(quote.result) > zquote ) {
					zquote = Number(quote.result);
					feeIdx = idx;
				}
				idx++;
			}
			if( feeIdx > -1 ) {
				return feesArr[feeIdx];
			}
			throw new Error(`failed gaining a valid fee for token "${token.label}"`);
		}
		throw new Error(`failed gaining any valid quotes for token "${token.label}"`);
	}
	throw new Error(`token ${token.label} does not have a valid fees array`);
}

export declare type ErrorCode = "CANCELLED"|"UNDERFUND"|"UNHANDLED"|"TOLARGE"|"UNKNOWN";


export const getErrorResponse = ( error: unknown, ):ResolverErrorResult => {

	if(error instanceof Error) {
		if( isErrorWithCause(error) ) {
			// user has optioned to cancel the transaction
			if(
				error.cause.name === "UserRejectedRequestError" ||
				error.cause.code === -32603 //BitGet incorrectly throwing "InternalRpcError" as the name, but this code covers it.
			) {
				return {
					ok: false,
					status: "warning",
					message: "Transaction cancelled by user request.",
					retry: false,
					code: "CANCELLED",
				};
			}

			if( error.cause.reason === "LS" ) {
				return {
					ok: false,
					status: "error",
					message: "This enormous stake is beyond what liquidity pools can offer. Try smaller amounts.",
					retry: false,
					code: "TOLARGE",
				}
			}

			// time to filter through known causes for a vanilla reversion error.
			if( error.cause.name === "ContractFunctionRevertedError" ) {

				// user has requested an amount which would exceed their balance
				if( error.cause.reason === "ERC20: transfer amount exceeds balance" ) {

					return {
						ok: false,
						status: "error",
						message: "Transaction would exceed your balance",
						retry: false,
						code: "UNDERFUND",
					};

				}
			}
		}
	}

	// bail out with the exception unhandled.
	return {
		ok: false,
		status: "error",
		message: "Something went wrong. Please try again later.",
		retry: false,
		code: "UNHANDLED",
	};

} 

export const pause = async ( timeout = 250 ) => {
	return new Promise(res => setTimeout(() => res(true), timeout));
}
